/* eslint-disable no-restricted-globals */
import React, { useState, useEffect, useRef } from 'react';
import * as signalR from '@microsoft/signalr';
import {
  Match,
  MatchClient,
  BracketClient,
  TournamentClient,
  ScoreType,
  ScoreModel,
  EndMatchModel
} from '../../utils/apiClient';
import { useAuth } from '../Auth/AuthContext';
import axios from 'axios';
import ApiUrl from '../../utils/config';
import './MatchConduct.scss';
import AppContext from '../../AppContext';
import { handleError } from '../../utils';
import { createMatchHubConnection, MatchLiveState } from '../../utils/matchHub';
import { getMatNumber, setMatNumber } from '../../utils/matSettings';

export interface MatchConductProps {
  matchId: number;
  onStartedChange?: (isStarted: boolean) => void;
  onFinished?: (winnerName: string) => void;
}

const MatchConduct: React.FC<MatchConductProps> = ({ matchId, onStartedChange, onFinished }) => {
  const appContext = React.useContext(AppContext);
  const [match, setMatch] = useState<Match | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [timerRunning, setTimerRunning] = useState(false);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const { isAdmin, hasRole } = useAuth();

  const matchClient = new MatchClient(ApiUrl, axios);
  const bracketClient = new BracketClient(ApiUrl, axios);
  const tournamentClient = new TournamentClient(ApiUrl, axios);

  const [localStarted, setLocalStarted] = useState<boolean>(false);
  const [localPaused, setLocalPaused] = useState<boolean>(false);
  const [localStartAt, setLocalStartAt] = useState<number | null>(null);
  const [localPausedAccumMs, setLocalPausedAccumMs] = useState<number>(0);
  const [localLastPauseAt, setLocalLastPauseAt] = useState<number | null>(null);

  const [homeIppon, setHomeIppon] = useState<number>(0);
  const [homeWazaAri, setHomeWazaAri] = useState<number>(0);
  const [homeYuko, setHomeYuko] = useState<number>(0);
  const [awayIppon, setAwayIppon] = useState<number>(0);
  const [awayWazaAri, setAwayWazaAri] = useState<number>(0);
  const [awayYuko, setAwayYuko] = useState<number>(0);

  const [homeShido, setHomeShido] = useState<number>(0);
  const [awayShido, setAwayShido] = useState<number>(0);

  const [osaekomiActive, setOsaekomiActive] = useState<'home' | 'away' | null>(null);
  const [osaekomiStartTime, setOsaekomiStartTime] = useState<number | null>(null);
  const [osaekomiCurrentTime, setOsaekomiCurrentTime] = useState<number>(0);

  // Refs нужны, чтобы из таймеров и async-колбэков читать актуальные значения
  // без перегенерации функций на каждый ререндер.
  const matchRef = useRef<Match | undefined>(match);
  matchRef.current = match;
  const finishedHandledRef = useRef<boolean>(false);

  // Табло (страница /display/:matNumber): трансляция хода поединка на монитор зала.
  const [matNumber, setMatNumberState] = useState<string | null>(() => getMatNumber());
  const hubConnectionRef = useRef<signalR.HubConnection | null>(null);
  const latestLiveStateRef = useRef<MatchLiveState | null>(null);

  type MatchScoresSnapshot = {
    homeIppon: number;
    homeWazaAri: number;
    homeYuko: number;
    awayIppon: number;
    awayWazaAri: number;
    awayYuko: number;
    homeShido: number;
    awayShido: number;
  };

  const getScoresSnapshot = (): MatchScoresSnapshot => ({
    homeIppon,
    homeWazaAri,
    homeYuko,
    awayIppon,
    awayWazaAri,
    awayYuko,
    homeShido,
    awayShido,
  });

  useEffect(() => {
    onStartedChange?.(localStarted);
  }, [localStarted, onStartedChange]);

  useEffect(() => {
    const fetchMatch = async () => {
      try {
        setLoading(true);
        // Новый поединок (компонент переиспользуется без размонтирования) — сбрасываем
        // флаг «итог уже отправлен», иначе табло навсегда перестанет получать live-обновления.
        finishedHandledRef.current = false;
        const matchData = await matchClient.getMatch(matchId);
        setMatch(matchData);

        setHomeIppon(matchData.homeScoreIppon || 0);
        setHomeWazaAri(matchData.homeScoreWazaAri || 0);
        setHomeYuko(matchData.homeScoreYuko || 0);
        setAwayIppon(matchData.awayScoreIppon || 0);
        setAwayWazaAri(matchData.awayScoreWazaAri || 0);
        setAwayYuko(matchData.awayScoreYuko || 0);
        setHomeShido(matchData.homeShido || 0);
        setAwayShido(matchData.awayShido || 0);

        const bracketData = await bracketClient.getBracket(matchData.tournamentBracketId);
        const tournamentData = await tournamentClient.getTournament(bracketData.tournamentId);
        const durationMs = tournamentData.durationInMinutes * 60 * 1000;

        if (matchData.endTime && matchData.startTime) {
          const startTime = new Date(matchData.startTime).getTime();
          const endTime = new Date(matchData.endTime).getTime();
          const pausedMs = matchData.pausedTime?.totalMilliseconds || 0;
          const elapsedMs = (endTime - startTime) - pausedMs;
          const remainingMs = Math.max(0, durationMs - elapsedMs);
          setTimeRemaining(remainingMs);
          // Не дёргаем onFinished при загрузке уже завершённого поединка
          finishedHandledRef.current = true;
        } else {
          setTimeRemaining(durationMs);
        }
      } catch (ex) {
        handleError(appContext!.setNotificationData, ex);
      } finally {
        setLoading(false);
      }
    };

    fetchMatch();
  }, [matchId]);

  // Подключение к хабу табло. Открываем только если на этом ноутбуке уже выбран ковёр —
  // без этого просто некуда транслировать (табло слушает конкретную группу "mat-N").
  useEffect(() => {
    if (!matNumber) return;

    const connection = createMatchHubConnection();
    hubConnectionRef.current = connection;

    // Отдаём то, что уже насчитано к этому моменту (см. эффект ниже — он пишет сюда
    // при каждом изменении, даже если в момент изменения соединение ещё не было готово).
    const resendCurrentState = () => {
      // Поединок уже завершён и итог отправлен — не затираем баннер победителя
      // устаревшим «живым» состоянием (актуально при переподключении/повторном RequestState).
      if (finishedHandledRef.current) return;
      if (latestLiveStateRef.current) {
        connection.invoke('PushMatchState', matNumber, latestLiveStateRef.current)
          .catch(err => console.error('[табло] не удалось отправить состояние поединка', err));
      }
    };

    connection.on('StateRequested', resendCurrentState);

    connection.start()
      .then(() => connection.invoke('JoinMat', matNumber))
      // Важно: подключение асинхронное, и первая попытка отправить счёт (в эффекте ниже)
      // почти всегда происходит РАНЬШЕ, чем соединение реально становится Connected —
      // та попытка просто тихо пропускается. Поэтому сразу после успешного JoinMat
      // явно повторяем отправку последнего известного состояния, а не ждём следующего клика судьи.
      .then(resendCurrentState)
      .catch(err => console.error('[табло] не удалось подключиться к хабу', err));

    connection.onreconnected(() => {
      connection.invoke('JoinMat', matNumber)
        .then(resendCurrentState)
        .catch(err => console.error('[табло] не удалось переподключиться к хабу', err));
    });

    return () => {
      connection.off('StateRequested', resendCurrentState);
      connection.stop();
      hubConnectionRef.current = null;
    };
  }, [matNumber]);

  // Автоматическая трансляция счёта на табло при любом изменении, которое видно на этой панели.
  // Специально не завязано на «тик» таймера каждую секунду — табло само крутит отсчёт
  // локально от timerAnchorUtc, чтобы не дёргаться при сетевых заминках.
  useEffect(() => {
    const connection = hubConnectionRef.current;
    if (!connection || !matNumber || !match) return;
    // Поединок уже завершён (итог отправлен через PushMatchEnded) — дальнейшие изменения
    // состояния (сброс таймера, обновление match после сохранения результата и т.п.)
    // не должны переотправлять «живой» счёт: это перезатирало баннер победителя на табло
    // почти сразу после его появления.
    if (finishedHandledRef.current) return;

    const homeName = match.homeParticipant?.person
      ? `${match.homeParticipant.person.lastName} ${match.homeParticipant.person.firstName}`.trim()
      : 'Первый участник';
    const awayName = match.awayParticipant?.person
      ? `${match.awayParticipant.person.lastName} ${match.awayParticipant.person.firstName}`.trim()
      : 'Второй участник';

    const state: MatchLiveState = {
      matchId: match.id || matchId,
      homeName,
      awayName,
      homeIppon,
      homeWazaAri,
      homeYuko,
      homeShido,
      awayIppon,
      awayWazaAri,
      awayYuko,
      awayShido,
      isStarted: localStarted,
      isPaused: localPaused,
      timerAnchorUtc: new Date().toISOString(),
      timeRemainingMsAtAnchor: timeRemaining,
      isTimerRunning: timerRunning,
      osaekomiSide: osaekomiActive,
      osaekomiStartedUtc: osaekomiStartTime ? new Date(osaekomiStartTime).toISOString() : null,
    };

    latestLiveStateRef.current = state;

    if (connection.state === signalR.HubConnectionState.Connected) {
      connection.invoke('PushMatchState', matNumber, state)
        .catch(err => console.error('[табло] не удалось отправить обновление счёта', err));
    }
    // Если соединение ещё не Connected — не страшно, latestLiveStateRef уже обновлён,
    // и как только JoinMat завершится, эффект подключения сам отправит актуальное состояние.
  }, [
    matNumber,
    match,
    matchId,
    homeIppon,
    homeWazaAri,
    homeYuko,
    homeShido,
    awayIppon,
    awayWazaAri,
    awayYuko,
    awayShido,
    localStarted,
    localPaused,
    timerRunning,
    osaekomiActive,
    osaekomiStartTime,
  ]);

  const handlePickMat = (value: string) => {
    setMatNumber(value);
    setMatNumberState(value);
  };

  useEffect(() => {
    if (timerRunning && timeRemaining > 0) {
      const interval = setInterval(() => {
        setTimeRemaining(prev => {
          const newTime = prev - 1000;
          if (newTime <= 0) {
            clearInterval(interval);
            setTimerRunning(false);
            handleTimeExpired();
            return 0;
          }
          return newTime;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [timerRunning, timeRemaining]);

  useEffect(() => {
    if (osaekomiActive && osaekomiStartTime) {
      const interval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - osaekomiStartTime) / 1000);
        setOsaekomiCurrentTime(elapsed);

        if (elapsed >= 20) {
          handleOsaekomiStop();
        }
      }, 100);

      return () => clearInterval(interval);
    } else {
      setOsaekomiCurrentTime(0);
    }
  }, [osaekomiActive, osaekomiStartTime]);

  const formatTime = (ms: number) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const canManageMatch = () => isAdmin() || hasRole('Судья');

  const handleStartLocal = () => {
    if (!canManageMatch() || !match) return;
    setLocalStarted(true);
    setLocalPaused(false);
    setLocalStartAt(Date.now());
    setTimerRunning(true);
    setActionSuccess('Поединок начат');
    setTimeout(() => setActionSuccess(null), 2000);
  };

  const handlePauseToggleLocal = () => {
    if (!canManageMatch() || !localStarted) return;
    if (!localPaused) {
      setLocalPaused(true);
      setLocalLastPauseAt(Date.now());
      setTimerRunning(false);
    } else {
      setLocalPaused(false);
      if (localLastPauseAt) {
        setLocalPausedAccumMs(prev => prev + (Date.now() - localLastPauseAt));
      }
      setLocalLastPauseAt(null);
      setTimerRunning(true);
    }
  };

  const submitViaBuiltInEndpoints = async (isHomeWinner: boolean) => {
    if (!match) return;

    try {
      const fresh = await matchClient.getMatch(match.id!);
      if (!fresh.startTime) {
        await matchClient.startMatch(match.id!);
      }
    } catch { }

    const addMany = async (type: ScoreType, count: number, isHome: boolean) => {
      for (let i = 0; i < count; i++) {
        await matchClient.addScore(match.id!, new ScoreModel({ scoreType: type, isHome }));
      }
    };

    await addMany(ScoreType.Yuko, homeYuko, true);
    await addMany(ScoreType.Yuko, awayYuko, false);

    await addMany(ScoreType.WazaAri, homeWazaAri, true);
    await addMany(ScoreType.WazaAri, awayWazaAri, false);

    if (homeIppon > 0 || awayIppon > 0) {
      if (homeIppon > 0) {
        await addMany(ScoreType.Ippon, 1, true);
      } else {
        await addMany(ScoreType.Ippon, 1, false);
      }
    }

    const afterScores = await matchClient.getMatch(match.id!);
    if (!afterScores.endTime) {
      await matchClient.endMatch(match.id!, new EndMatchModel({ isHomeWinner }));
    }
  };

  const finalizeAndSubmit = async (isHomeWinner: boolean, scoresOverride?: MatchScoresSnapshot) => {
    if (!match) return;
    const scores = scoresOverride ?? getScoresSnapshot();
    try {
      setTimerRunning(false);
      const payload = {
        isHomeWinner,
        homeScoreIppon: scores.homeIppon,
        homeScoreWazaAri: scores.homeWazaAri,
        homeScoreYuko: scores.homeYuko,
        awayScoreIppon: scores.awayIppon,
        awayScoreWazaAri: scores.awayWazaAri,
        awayScoreYuko: scores.awayYuko,
        homeShido: scores.homeShido,
        awayShido: scores.awayShido,
        startTime: localStartAt ? new Date(localStartAt).toISOString() : null,
        pausedMilliseconds: localPausedAccumMs
      } as any;

      await axios.post(`${ApiUrl}/api/Match/SubmitFinalResult?id=${match.id}`, payload);

      setActionSuccess('Результат поединка сохранен');
      setTimeout(() => setActionSuccess(null), 3000);

      const updated = await matchClient.getMatch(match.id!);
      setMatch(updated);
      notifyFinished(updated, isHomeWinner);
    } catch (e) {
      try {
        await submitViaBuiltInEndpoints(isHomeWinner);
        setActionSuccess('Результат поединка сохранен');
        setTimeout(() => setActionSuccess(null), 3000);
        const updated = await matchClient.getMatch(match.id!);
        setMatch(updated);
        notifyFinished(updated, isHomeWinner);
      } catch (ex) {
        handleError(appContext!.setNotificationData, ex);
      }
    }
  };

  const notifyFinished = (updated: Match, isHomeWinner: boolean) => {
    if (finishedHandledRef.current) return;
    finishedHandledRef.current = true;
    const winnerName = isHomeWinner
      ? `${updated.homeParticipant?.person?.lastName || ''} ${updated.homeParticipant?.person?.firstName || ''}`.trim()
      : `${updated.awayParticipant?.person?.lastName || ''} ${updated.awayParticipant?.person?.firstName || ''}`.trim();

    const connection = hubConnectionRef.current;
    if (connection && matNumber && connection.state === signalR.HubConnectionState.Connected) {
      connection.invoke('PushMatchEnded', matNumber, {
        matchId: updated.id || matchId,
        winnerName: winnerName || 'Победитель',
      }).catch(err => console.error('[табло] не удалось отправить итог поединка', err));
    }

    onFinished?.(winnerName || 'Победитель');
  };

  const handleScoreLocal = (type: 'ippon' | 'waza' | 'yuko', isHome: boolean) => {
    if (!canManageMatch() || !localStarted || localPaused) return;

    if (type === 'ippon') {
      const scores = getScoresSnapshot();
      if (isHome) {
        scores.homeIppon += 1;
        setHomeIppon(scores.homeIppon);
      } else {
        scores.awayIppon += 1;
        setAwayIppon(scores.awayIppon);
      }
      finalizeAndSubmit(isHome, scores);
      return;
    }

    if (type === 'waza') {
      const scores = getScoresSnapshot();
      if (isHome) {
        scores.homeWazaAri += 1;
        setHomeWazaAri(scores.homeWazaAri);
        if (scores.homeWazaAri >= 2) finalizeAndSubmit(true, scores);
      } else {
        scores.awayWazaAri += 1;
        setAwayWazaAri(scores.awayWazaAri);
        if (scores.awayWazaAri >= 2) finalizeAndSubmit(false, scores);
      }
      return;
    }

    if (type === 'yuko') {
      if (isHome) setHomeYuko(v => v + 1);
      else setAwayYuko(v => v + 1);
    }
  };

  const handleShido = (isHome: boolean) => {
    if (!canManageMatch() || !localStarted || localPaused) return;

    const scores = getScoresSnapshot();
    if (isHome) {
      scores.homeShido += 1;
      setHomeShido(scores.homeShido);
      if (scores.homeShido >= 3) finalizeAndSubmit(false, scores);
    } else {
      scores.awayShido += 1;
      setAwayShido(scores.awayShido);
      if (scores.awayShido >= 3) finalizeAndSubmit(true, scores);
    }
  };

  const handleOsaekomiStart = (isHome: boolean) => {
    if (!canManageMatch() || !localStarted || localPaused || osaekomiActive) return;
    setOsaekomiActive(isHome ? 'home' : 'away');
    setOsaekomiStartTime(Date.now());
  };

  const handleOsaekomiStop = () => {
    if (!osaekomiActive || !osaekomiStartTime) return;

    const elapsedSeconds = Math.floor((Date.now() - osaekomiStartTime) / 1000);

    if (osaekomiActive === 'home') {
      checkOsaekomiScore(elapsedSeconds, true);
    } else {
      checkOsaekomiScore(elapsedSeconds, false);
    }

    setOsaekomiActive(null);
    setOsaekomiStartTime(null);
    setOsaekomiCurrentTime(0);
  };

  // Правила удержания старших возрастов: 5-9с=Юко, 10-19с=Вазари, 20+с=Иппон
  const checkOsaekomiScore = (seconds: number, isHome: boolean) => {
    const scores = getScoresSnapshot();

    if (seconds >= 20) {
      if (isHome) {
        scores.homeIppon += 1;
        setHomeIppon(scores.homeIppon);
      } else {
        scores.awayIppon += 1;
        setAwayIppon(scores.awayIppon);
      }
      finalizeAndSubmit(isHome, scores);
    } else if (seconds >= 10) {
      if (isHome) {
        scores.homeWazaAri += 1;
        setHomeWazaAri(scores.homeWazaAri);
        if (scores.homeWazaAri >= 2) finalizeAndSubmit(true, scores);
      } else {
        scores.awayWazaAri += 1;
        setAwayWazaAri(scores.awayWazaAri);
        if (scores.awayWazaAri >= 2) finalizeAndSubmit(false, scores);
      }
    } else if (seconds >= 5) {
      if (isHome) {
        scores.homeYuko += 1;
        setHomeYuko(scores.homeYuko);
      } else {
        scores.awayYuko += 1;
        setAwayYuko(scores.awayYuko);
      }
    }
  };

  const handleTimeExpired = () => {
    if (osaekomiActive) {
      handleOsaekomiStop();
    }

    if (homeWazaAri !== awayWazaAri) {
      finalizeAndSubmit(homeWazaAri > awayWazaAri);
      return;
    }
    if (homeYuko !== awayYuko) {
      finalizeAndSubmit(homeYuko > awayYuko);
      return;
    }
    setActionSuccess('Время истекло. Выберите победителя.');
    setTimeout(() => setActionSuccess(null), 3000);
  };

  if (loading) {
    return <div className="match-conduct__loading">Загрузка данных поединка...</div>;
  }

  if (!match) {
    return <div className="match-conduct__loading">Поединок не найден</div>;
  }

  const homeName = match.homeParticipant?.person
    ? `${match.homeParticipant.person.lastName} ${match.homeParticipant.person.firstName}`
    : 'Первый участник';
  const awayName = match.awayParticipant?.person
    ? `${match.awayParticipant.person.lastName} ${match.awayParticipant.person.firstName}`
    : 'Второй участник';

  const isMatchFinished = match.endTime && match.isHomeWinner !== undefined;

  return (
    <div className="match-conduct">
      {canManageMatch() && (
        <div className="match-conduct__mat-picker">
          {matNumber ? (
            <span>
              Ковёр №{matNumber} · табло транслируется{' '}
              <button type="button" className="match-conduct__mat-picker-change" onClick={() => setMatNumberState(null)}>
                изменить
              </button>
            </span>
          ) : (
            <span>
              Выберите ковёр для табло:{' '}
              {['1', '2', '3', '4'].map(n => (
                <button key={n} type="button" onClick={() => handlePickMat(n)}>
                  {n}
                </button>
              ))}
            </span>
          )}
        </div>
      )}

      {actionSuccess && <div className="match-conduct__success">{actionSuccess}</div>}

      {isMatchFinished && (
        <div className="match-conduct__finished-banner">
          <h3>Поединок завершён</h3>
          <p>
            <strong>Победитель:</strong> {match.isHomeWinner ? homeName : awayName}
          </p>
        </div>
      )}

      <div className={`match-conduct__timer ${osaekomiActive ? 'osaekomi-active' : ''}`}>
        <h3>{formatTime(timeRemaining)}</h3>
        {osaekomiActive && (
          <div className="osaekomi-indicator">
            УДЕРЖАНИЕ ({osaekomiActive === 'home' ? homeName : awayName}): {osaekomiCurrentTime}с
          </div>
        )}
      </div>

      <div className="match-participants">
        <div className={`participant home ${(localStarted && !localPaused && (homeWazaAri >= 2 || homeIppon > 0)) || (isMatchFinished && match.isHomeWinner) ? 'winner' : ''}`}>
          <p className="participant-name">{homeName}</p>
          <div className="score-display">
            <div className="score-item">
              <span>Иппон: {homeIppon}</span>
              {localStarted && !localPaused && !isMatchFinished && (
                <button onClick={() => handleScoreLocal('ippon', true)} className="score-button">Иппон</button>
              )}
            </div>
            <div className="score-item">
              <span>Вазари: {homeWazaAri}</span>
              {localStarted && !localPaused && !isMatchFinished && (
                <button onClick={() => handleScoreLocal('waza', true)} className="score-button">Вазари</button>
              )}
            </div>
            <div className="score-item">
              <span>Юко: {homeYuko}</span>
              {localStarted && !localPaused && !isMatchFinished && (
                <button onClick={() => handleScoreLocal('yuko', true)} className="score-button">Юко</button>
              )}
            </div>
            <div className="score-item">
              <span style={{ color: '#f59e0b' }}>Шидо: {homeShido}</span>
              {localStarted && !localPaused && !isMatchFinished && (
                <button onClick={() => handleShido(true)} className="score-button shido-button">Шидо</button>
              )}
            </div>
            {!isMatchFinished && (
              <div className="score-item">
                <span>Удержание</span>
                {localStarted && !localPaused && !osaekomiActive && (
                  <button onClick={() => handleOsaekomiStart(true)} className="score-button osaekomi-button">Начать</button>
                )}
                {osaekomiActive === 'home' && (
                  <button onClick={handleOsaekomiStop} className="score-button osaekomi-stop-button">МАТЕ</button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="vs-display">против</div>

        <div className={`participant participantAway ${(localStarted && !localPaused && (awayWazaAri >= 2 || awayIppon > 0)) || (isMatchFinished && !match.isHomeWinner) ? 'winner' : ''}`}>
          <p className="participant-name" style={{ textAlign: 'right' }}>{awayName}</p>
          <div className="score-display">
            <div className="score-item">
              {localStarted && !localPaused && !isMatchFinished && (
                <button onClick={() => handleScoreLocal('ippon', false)} className="score-button">Иппон</button>
              )}
              <span>Иппон: {awayIppon}</span>
            </div>
            <div className="score-item">
              {localStarted && !localPaused && !isMatchFinished && (
                <button onClick={() => handleScoreLocal('waza', false)} className="score-button">Вазари</button>
              )}
              <span>Вазари: {awayWazaAri}</span>
            </div>
            <div className="score-item">
              {localStarted && !localPaused && !isMatchFinished && (
                <button onClick={() => handleScoreLocal('yuko', false)} className="score-button">Юко</button>
              )}
              <span>Юко: {awayYuko}</span>
            </div>
            <div className="score-item">
              {localStarted && !localPaused && !isMatchFinished && (
                <button onClick={() => handleShido(false)} className="score-button shido-button">Шидо</button>
              )}
              <span style={{ color: '#f59e0b' }}>Шидо: {awayShido}</span>
            </div>
            {!isMatchFinished && (
              <div className="score-item">
                {localStarted && !localPaused && !osaekomiActive && (
                  <button onClick={() => handleOsaekomiStart(false)} className="score-button osaekomi-button">Начать</button>
                )}
                {osaekomiActive === 'away' && (
                  <button onClick={handleOsaekomiStop} className="score-button osaekomi-stop-button">МАТЕ</button>
                )}
                <span>Удержание</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {canManageMatch() && !isMatchFinished && (
        <div className="match-conduct__actions">
          {!localStarted && (
            <button onClick={handleStartLocal} className="start-button">Начать поединок</button>
          )}

          {localStarted && (
            <button onClick={handlePauseToggleLocal} className={localPaused ? 'resume-button' : 'pause-button'}>
              {localPaused ? 'Возобновить' : 'Пауза'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default MatchConduct;
